import * as d3 from 'd3';
import moment from 'moment';
import { html, css, svg } from '../../utility/lit-entity';
import { sharedStyles } from '../../styles/shared-styles';
import '../../common/bn-icon';
import '../../common/bn-loading';
import './device-events';

const TOP_MARGIN = 16;
const BOTTOM_MARGIN = 2;
const PADDING_BASE = 3;
const TAB_HEIGHT = TOP_MARGIN - PADDING_BASE;
const TIGHT_MARGIN = -0.025;
const HOURS_PER_READING = 4;
const READINGS_PER_DAY = 24 / HOURS_PER_READING;
const SILO_READINGS_PER_DAY = (60 / 5) * 24;
const PADDING_BASE_DOUBLE = PADDING_BASE * 2;
const REPORT_CADENCE_S = 60 * 60 * HOURS_PER_READING;
const NO_REPORT_TOLERANCE_S = REPORT_CADENCE_S + (60 * 60);
const INSET_SCALE = 1.25;
const DASH_PATTERN_WIDTH = 6;

export const DeviceMonitoringChartLayerMixin = (superClass) => class extends superClass {
  constructor() {
    super();
    this.levels = [];
    this.events = [];
  }

  static get properties() {
    return {
      hide: { type: Boolean, reflect: true },
      hardwareType: { type: String, attribute: 'hardware-type' },
      levels: { type: Array },
      events: { type: Array },
      totalVolumes: { type: Object, attribute: 'total-volumes' },
      maxTotalVolume: { type: Number, attribute: false },
      start: { type: String },
      end: { type: String },
      hasData: { type: Boolean, attribute: false },
    };
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'total-volumes') {
      const parsedValue = JSON.parse(newValue);
      this.maxTotalVolume = Math.max(...Object.values(parsedValue).map((val) => Number(val)));
    }

    super.attributeChangedCallback(name, oldValue, newValue);
  }

  static get styles() {
    return [sharedStyles, css`
      :host {
        display: flex;
        align-content: stretch;
        min-height: 100px;
        width: 100%;
        pointer-events: none;
      }
      :host([hide]) {
        display: none;
      }
      bn-loading {
        flex-grow: 1;
        width: 100%;
        height: 100%;
      }
      svg {
        height: 100%;
        width: 100%;
        display: block;
        cursor: pointer;
      }
      .hide {
        display: none;
      }
      .pointer {
        pointer-events: all;
      }
      .no-pointer {
        pointer-events: none;
      }
    `];
  }

  constants() {
    return {
      TOP_MARGIN,
      BOTTOM_MARGIN,
      PADDING_BASE,
      TAB_HEIGHT,
      TIGHT_MARGIN,
      HOURS_PER_READING,
      PADDING_BASE_DOUBLE,
      REPORT_CADENCE_S,
      NO_REPORT_TOLERANCE_S,
      INSET_SCALE,
      READINGS_PER_DAY,
    };
  }

  // Override this method to render chart data
  renderLayerSVG() {
    return svg``;
  }

  // Do not use render directly, override renderLayerSVG instead
  render() {
    if (this.hide) {
      return html``;
    }

    return html`
      ${this._renderChart()}
    `;
  }

  getSelectableClassList(event) {
    return ['selectable', `selectable-${event.unixEpoch}`];
  }

  setSelectedDate(unixEpoch) {
    const currentlySelected = this.shadowRoot.querySelectorAll('.selectable.selected');
    currentlySelected.forEach((element) => element.classList.remove('selected'));

    const selected = this.shadowRoot.querySelectorAll(`.selectable-${unixEpoch}`);
    if (selected.length === 0) {
      return;
    }

    selected.forEach((element) => element.classList.add('selected'));
  }

  setHoveredDate(unixEpoch) {
    const currentlyHovered = this.shadowRoot.querySelectorAll('.selectable.hovered');
    currentlyHovered.forEach((element) => element.classList.remove('hovered'));

    const hovered = this.shadowRoot.querySelectorAll(`.selectable-${unixEpoch}`);
    if (hovered.length === 0) {
      return;
    }

    hovered.forEach((element) => element.classList.add('hovered'));
  }

  _renderChart() {
    if (!this._hasData()) {
      return html`<bn-loading></bn-loading>`;
    }

    this.startMoment = moment(this.start);
    this.endMoment = moment(this.end);
    this.dayCount = this.endMoment.diff(this.startMoment, 'days');

    this.width = this.shadowRoot.host.clientWidth;
    this.height = this.shadowRoot.host.clientHeight;

    this.xScale = d3.scaleTime().range([0, this.width]).domain([this.startMoment, this.endMoment]);

    switch (this.hardwareType) {
    case 'siloSensor':
      this.padding = 0;
      this.paddingDouble = this.padding * 2;

      this.levelWidth = Math.max(this.width / this.dayCount / SILO_READINGS_PER_DAY, 2);
      this.levelWidthPadded = this.levelWidth;
      this.barHeightPadded = this.height - TOP_MARGIN - BOTTOM_MARGIN;
      this.barHeight = this.barHeightPadded - PADDING_BASE_DOUBLE;
      break;
    default:
      this.padding = PADDING_BASE * (7 / this.dayCount);
      this.padding = this.padding < 1 ? TIGHT_MARGIN : this.padding;
      this.paddingDouble = this.padding * 2;

      this.levelWidthPadded = Math.max(this.width / this.dayCount / READINGS_PER_DAY, 2);
      this.levelWidth = Math.max(this.levelWidthPadded - this.paddingDouble, 2);
      this.barHeightPadded = this.height - TOP_MARGIN - BOTTOM_MARGIN;
      this.barHeight = this.barHeightPadded - PADDING_BASE_DOUBLE;
      break;
    }

    return svg`
      <svg
        viewBox="0 0 ${this.width} ${this.height}"
        width="100%"
        height="${this.height}"
      >
        <g transform="translate(0, ${TOP_MARGIN})">
          ${this.renderLayerSVG()}
        </g>
      </svg>
    `;
  }

  getDashedRightPattern({ id, fill, stroke }) {
    return svg`
      <pattern id="${id}" width="${DASH_PATTERN_WIDTH}" height="${DASH_PATTERN_WIDTH}" patternUnits="userSpaceOnUse">
        <rect fill="${fill}" stroke="transparent" x="0" y="0" width="${DASH_PATTERN_WIDTH}" height="${DASH_PATTERN_WIDTH}"/>
        <path stroke="${stroke}" stroke-linecap="square" stroke-width="1" d="M ${-DASH_PATTERN_WIDTH},${DASH_PATTERN_WIDTH} L 0,0"/>
        <path stroke="${stroke}" stroke-linecap="square" stroke-width="1" d="M 0,${DASH_PATTERN_WIDTH} L ${DASH_PATTERN_WIDTH},0"/>
        <path stroke="${stroke}" stroke-linecap="square" stroke-width="1" d="M ${DASH_PATTERN_WIDTH},${DASH_PATTERN_WIDTH} L ${DASH_PATTERN_WIDTH * 2},0"/>
      </pattern>
    `;
  }

  _hasData() {
    return this.totalVolumes !== undefined;
  }

  connectedCallback() {
    super.connectedCallback();
    this._onResize = this._onResize.bind(this);
    window.addEventListener('resize', this._onResize);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this._onResize);
  }

  _onResize() {
    if (!this.resizeThrottle) {
      this.requestUpdate();
      this.resizeHandle = setTimeout(() => {
        clearTimeout(this.resizeHandle);
        this.requestUpdate();
      }, 200);
    }
  }
};
